/**
 * #731 — the order TABLE row boundary. Sibling of `OrderCardRow`.
 *
 * The table's rows were inline <tr> JSX inside a 7,361-line parent, so they had
 * no boundary at all: every publication recreated all ~940 of them and
 * reconciled the lot. Measured, the table path cost ~1,330ms of commit per
 * publication against ~353ms for the card path on the same data — both the
 * unbounded path and the more expensive one.
 *
 * STEP 1 of three, deliberately: the body below is lifted VERBATIM, including
 * both venue branches, and every value it used to close over is now an explicit
 * prop. TypeScript enumerated that list rather than me reading 648 lines and
 * hoping. This step changes no behaviour and bounds nothing — it exists so the
 * move itself can be proven by a byte-diff of the rendered page before the
 * bounding change lands on top of it.
 */
import { memo, type Dispatch, type SetStateAction } from 'react';
import type { SortedOrderRow } from './orderSort';
import type { PolyRelayOpenOrder } from '../../lib/polymarket/relay';
import type { EventStateCertainty } from '../../lib/sport/eventStateCertainty';
import type { DivergenceInfo } from '../../lib/divergence/polyKalshiDivergence';
import type { KalshiCancelState } from '../../lib/trading/kalshiCancelLifecycle';
import type { PolyOrderDescription } from '../../lib/polymarket/describeOrder';
import type { OrderCardVM } from '../../components/cards/orderCardModel';
import type { EditOrderData, SameGameRowRef } from './index';
import {
} from '../../services/analyticsGameKey';
import { isKalshiCancelPending } from '../../lib/trading/kalshiCancelLifecycle';
import { describePolyCancelRow } from '../../lib/trading/polyCancelPresentation';
import type { PolyCancelStates } from '../../lib/trading/bulkOrderCancel';
import { VenueChip } from '../../components/atoms/VenueChip';
import { GameDetailLink } from '../../components/atoms/GameDetailLink';
import { EventStateWarning } from '../../components/atoms/EventStateWarning';
import { OrderTifCell, PolyOrderTifCell } from './orderTifCell';
import { OrderActionButtons } from '../../components/molecules';
import {
  describePolyOrderCached,
  polyOrderLiveData,
} from '../../lib/polymarket/describeOrder';
import { polyOrderQueueFromBook } from '../../lib/polymarket/orderQueue';
import { mirrorCents } from '../../lib/pricing/subCent';
import { describeMarket } from '../../lib/kalshiTicker';
import {
  BetTeamTitle,
  PinToggle,
  VelocityMuteToggle,
} from '../../components/cards/cardKit';
import {
  fmtUsd,
} from '../../components/cards/cardFormat';
import { DivergenceBadge } from '../../components/cards/DivergenceBadge';
import {
  kalshiOrderFillStats,
  openOrderEconomics,
  openOrderQuote,
  queueBehindTone,
  queueRowTone,
  queueTone,
  queueValuePulse,
} from '../../components/cards/orderCardModel';
import {
  sideNativePriceDelta,
} from '../../components/cards/priceVelocity';
import {
  gameDetailHref,
  formatStartTimeLocal,
  inLiveFeed,
  orderChipHref,
  polyTableLegacyGameHref,
  PriceWithOddsInline,
} from './index';


/* ── #731 step 2: the row's market inputs, resolved ONCE in the parent ──────
 *
 * The row used to read the market store imperatively — getLastTradeYesCents,
 * getOrderbookRaw, polyOrderLiveData — each gated on `void marketVersion`. That
 * made `marketVersion` a prop, which changes on every publication, so `memo`
 * could never bail and every one of the 858 rows re-ran for a single moved
 * ticker.
 *
 * Everything those reads produce is a handful of SCALARS: one number off the
 * Kalshi book, two off the poly book, plus map lookups. Resolving them in the
 * parent and value-stabilising the result (see `stableVm.ts`) makes the row
 * pure with respect to its props: it re-renders exactly when ITS OWN market
 * data moved, and not otherwise.
 *
 * Nothing is cached, delayed or served stale — the resolver runs on every
 * publication, exactly as the inline reads did. Only the IDENTITY of its output
 * is reused, and only when every value in it is unchanged.
 *
 * The two poly projections below are exported and used by BOTH the resolver and
 * the row, so the queue figure can never be computed from a different `isSell`
 * or price than the one the row renders.
 */

export function polyOrderId(o: PolyRelayOpenOrder): string {
  return String(o.id ?? o.orderID ?? o.orderId ?? '');
}
export function polyIsSell(o: PolyRelayOpenOrder): boolean {
  return String(o.side ?? '').toUpperCase() === 'SELL';
}
export function polyPriceCents(o: PolyRelayOpenOrder): number | null {
  const priceFrac = typeof o.price === 'number' ? o.price : Number(o.price ?? NaN);
  return Number.isFinite(priceFrac) ? Math.round(priceFrac * 10000) / 100 : null;
}
export function polyAssetId(o: PolyRelayOpenOrder): string {
  return String(o.asset_id ?? o.market ?? polyOrderId(o));
}

/** One row's resolved market inputs. Flat and comparable by value on purpose —
 *  a nested book or array here would never compare equal and would defeat the
 *  whole boundary (the store mutates books in place). */
export interface OrderTableRowMarket {
  bookRetained?: boolean;
  key: string;
  /* Kalshi */
  lastYes: number | null;
  topRef: number | null;
  filledQty: number | undefined;
  queue: number | null;
  queueAbove: number | null;
  queueBehind: number | null;
  queueTotal: number | null;
  makerRate: number | null;
  priceVelocityM1YesCents: number | null;
  divergence: DivergenceInfo | null;
  divergenceFlash: boolean;
  liveFeed: boolean;
  /* Polymarket */
  polyDesc: PolyOrderDescription | null;
  polyLastCents: number | null;
  polyQueue: number | null;
  polyQueueAbove: number | null;
  polyQueueTotal: number | null;
  polyBookDiff: number | null;
  polyEventStartMs: number | null;
  /* Both venues */
  velocitySeverity: 'alert' | 'warn' | null;
  certainty: EventStateCertainty;
  muted: boolean;
  pinned: boolean;
  canceling: boolean;
  /** #748 — poly rows only: cancel asked for, outcome unconfirmed. */
  cancelUnconfirmed: boolean;
  cancelTitle?: string;
}

/**
 * Read-only by declaration: the resolver only ever looks values up, and saying
 * so is what let the `as never` at the call site go. That cast was hiding a
 * real mismatch — the parent hands over ReadonlySet, and the interface claimed
 * Set. Benign here, but it is exactly the kind of thing a cast conceals.
 */
export interface OrderTableMarketDeps {
  isBookRetained?: (ticker: string) => boolean;
  getLastTradeYesCents: (t: string) => number | null;
  getMakerRate: (t: string) => number | null;
  getPriceVelocityM1YesCents: (t: string) => number | null;
  getOrderbookRaw: (t: string) => { yes: readonly { price: number }[]; no: readonly { price: number }[] } | null;
  queueMetricsByOrderId: ReadonlyMap<string, { queue: number | null; queueAbove: number | null; queueBehind: number | null; total: number | null } | undefined>;
  recentFilledQtyByOrderId: ReadonlyMap<string, number>;
  divergenceByTicker: ReadonlyMap<string, DivergenceInfo>;
  eventStateCertaintyByTicker: ReadonlyMap<string, EventStateCertainty>;
  velocitySpikeFlash: ReadonlyMap<string, 'alert' | 'warn'>;
  velocityMutedIds: ReadonlySet<string>;
  visibleDivergenceFlashTickers: ReadonlySet<string>;
  pinnedKeySet: ReadonlySet<string>;
  kalshiCancelStates: Record<string, KalshiCancelState | undefined>;
  polyCancelStates: PolyCancelStates;
}

/**
 * A market bundle with nothing in it. Exported as the render-time FALLBACK.
 *
 * The parent looks its bundle up by row id, and that lookup is built from the
 * same array the render walks, so it cannot miss today. It was written with a
 * `!` non-null assertion on that reasoning — which is exactly how a blank page
 * happens. The app has an `ErrorBoundary` (`components/layout/ErrorBoundary.tsx`),
 * but it is mounted around Manager and G4 ONLY — verified by grepping every
 * `<ErrorBoundary` in `src/`. Nothing wraps Position Tracker, so a throwing
 * row unmounts this page on `market.polyLastCents`, taking every
 * other order's row with it.
 * A row with no market data renders its order with empty live cells, which is
 * a bad row; a thrown error is no page at all. The bad row wins.
 */
export const EMPTY_ORDER_TABLE_MARKET: OrderTableRowMarket = {
  key: '',
  lastYes: null, topRef: null, filledQty: undefined,
  queue: null, queueAbove: null, queueBehind: null, queueTotal: null,
  makerRate: null, priceVelocityM1YesCents: null,
  divergence: null, divergenceFlash: false, liveFeed: false,
  polyDesc: null, polyLastCents: null,
  polyQueue: null, polyQueueAbove: null, polyQueueTotal: null,
  polyBookDiff: null, polyEventStartMs: null,
  velocitySeverity: null, certainty: 'unknown',
  muted: false, pinned: false, canceling: false, cancelUnconfirmed: false,
};

export function resolveOrderTableRowMarket(
  row: SortedOrderRow,
  key: string,
  d: OrderTableMarketDeps,
): OrderTableRowMarket {
  const base: OrderTableRowMarket = {
    ...EMPTY_ORDER_TABLE_MARKET,
    key,
  };

  if (row.kind === 'poly') {
    const o = row.order;
    const id = polyOrderId(o);
    const assetId = polyAssetId(o);
    const isSell = polyIsSell(o);
    const priceCents = polyPriceCents(o);
    const polyDesc = describePolyOrderCached(assetId);
    const live = polyDesc ? polyOrderLiveData(assetId, polyDesc.pmTicker) : null;
    const q =
      !isSell && priceCents != null && live
        ? polyOrderQueueFromBook(live.sideBids, priceCents)
        : null;
    const polyCancel = describePolyCancelRow(d.polyCancelStates[id]);
    return {
      ...base,
      polyDesc,
      polyLastCents: live?.lastCents ?? null,
      polyQueue: q?.queue ?? null,
      polyQueueAbove: q?.queueAbove ?? null,
      polyQueueTotal: q?.queueTotal ?? null,
      polyBookDiff: q?.bookDiff ?? null,
      polyEventStartMs: live?.eventStartMs ?? null,
      velocitySeverity: d.velocitySpikeFlash.get(`poly-${id}`) ?? null,
      certainty: d.eventStateCertaintyByTicker.get(assetId) ?? 'unknown',
      muted: d.velocityMutedIds.has(`poly-${id}`),
      pinned: d.pinnedKeySet.has(`poly-${id}`),
      // #748 — one shared answer, so this row cannot disagree with the
      // sidebar or the panel about the same order.
      canceling: polyCancel.pending,
      cancelUnconfirmed: polyCancel.unconfirmed,
      cancelTitle: polyCancel.message ?? undefined,
    };
  }

  const order = row.order;
  const isSell = order.action === 'sell';
  // The book contributes exactly one number: the top of the REFERENCE side.
  const book = d.getOrderbookRaw(order.ticker);
  let topRef: number | null = null;
  if (book) {
    const refLevels = isSell
      ? (order.side === 'yes' ? book.no : book.yes)
      : (order.side === 'yes' ? book.yes : book.no);
    if (refLevels.length > 0) {
      let max = -Infinity;
      for (const lvl of refLevels) {
        if (lvl.price > max) max = lvl.price;
      }
      if (Number.isFinite(max)) topRef = isSell ? 100 - max : max;
    }
  }
  const metrics = d.queueMetricsByOrderId.get(order.orderId);
  return {
    ...base,
    bookRetained: d.isBookRetained?.(order.ticker),
    lastYes: d.getLastTradeYesCents(order.ticker),
    topRef,
    filledQty: d.recentFilledQtyByOrderId.get(order.orderId),
    queue: metrics?.queue ?? null,
    queueAbove: metrics?.queueAbove ?? null,
    queueBehind: metrics?.queueBehind ?? null,
    queueTotal: metrics?.total ?? null,
    makerRate: d.getMakerRate(order.ticker),
    priceVelocityM1YesCents: d.getPriceVelocityM1YesCents(order.ticker),
    divergence: d.divergenceByTicker.get(order.ticker) ?? null,
    divergenceFlash: d.visibleDivergenceFlashTickers.has(order.ticker),
    liveFeed: inLiveFeed(order.ticker),
    velocitySeverity: d.velocitySpikeFlash.get(order.orderId) ?? null,
    certainty: d.eventStateCertaintyByTicker.get(order.ticker) ?? 'unknown',
    muted: d.velocityMutedIds.has(order.orderId),
    pinned: d.pinnedKeySet.has(order.orderId),
    canceling: isKalshiCancelPending(d.kalshiCancelStates[order.orderId]),
    cancelUnconfirmed: false,
  };
}

export interface OrderTableRowProps {
  /**
   * The discriminated union the body already branches on (`row.kind`). It was
   * `any` while the extraction was in flight — the compiler was being used to
   * ENUMERATE the props and the net was left off afterwards. On the component
   * that renders live prices, queue positions and P&L, that is precisely the
   * "more brittle data contract" this ticket forbids: a mistyped field access
   * would compile.
   */
  row: SortedOrderRow;
  /** #828 — stable row identity for the reading-position primitive, set by
   *  the parent to `orderRowId(row)`. Both branches carry it: a poly and a
   *  Kalshi row are the same kind of anchor to the pane above them. */
  viewAnchor?: string;
  /** #731 — this row's market inputs, resolved and identity-stabilised by the
   *  parent. The row reads NO store state of its own. */
  market: OrderTableRowMarket;
  feeAdjustedOdds: boolean;
  quoteRebatePct: number;
  openPolyOrderPanel: (assetId: string, fallbackHref: string | null) => void;
  orderCardOpen: (
    vm: Pick<OrderCardVM, 'venue' | 'ticker' | 'side'>,
    href: string | null,
  ) => () => void;
  requestCancelOrder: (target: {
    venue: OrderCardVM['venue'];
    orderId: string;
    label: string;
  }) => void;
  sameGameRow: (row: SameGameRowRef) => {
    className: string;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
  setEditOrder: Dispatch<SetStateAction<EditOrderData | null>>;
  togglePin: (entry: { key: string; kind: 'order'; label: string }) => void;
  toggleVelocityMute: (key: string) => void;
}

function OrderTableRowInner({
  row,
  market,
  feeAdjustedOdds,
  openPolyOrderPanel,
  orderCardOpen,
  quoteRebatePct,
  requestCancelOrder,
  sameGameRow,
  setEditOrder,
  togglePin,
  toggleVelocityMute,
  viewAnchor,
}: OrderTableRowProps) {
                    if (row.kind === 'poly') {
                      // Polymarket rows — card↔table parity via the same
                      // helpers buildPolyOrderVM uses: live last/book from
                      // polyOrderLiveData (slug-keyed, orientation-proof) and
                      // queue metrics from polyOrderQueueFromBook. Deliberate
                      // deltas from Kalshi: 1m Δ dashes (priceVelocity has no
                      // poly source yet) and In-line-behind dashes (no
                      // per-order FIFO slot on the CLOB — unknowable, not
                      // unwired).
                      const o = row.order;
                      const id = String(o.id ?? o.orderID ?? o.orderId ?? '');
                      const sideStr = String(o.side ?? '').toUpperCase();
                      const isSell = sideStr === 'SELL';
                      const priceFrac =
                        typeof o.price === 'number' ? o.price : Number(o.price ?? NaN);
                      const priceCents = Number.isFinite(priceFrac)
                        ? Math.round(priceFrac * 10000) / 100
                        : null;
                      // Polymarket carries no maker fee in our model, so the
                      // quote pair is the raw price under every Fees state.
                      const priceQuote = openOrderQuote({
                        priceCents,
                        feeAdjusted: false,
                        makerRate: 0,
                        isSell,
                      });
                      const orig =
                        typeof o.original_size === 'number'
                          ? o.original_size
                          : Number(o.original_size ?? 0);
                      const matched =
                        typeof o.size_matched === 'number'
                          ? o.size_matched
                          : Number(o.size_matched ?? 0);
                      const remaining = Math.max(0, orig - matched);
                      const filling = matched > 0;
                      const assetId = String(o.asset_id ?? o.market ?? id);
                      // Resolve to a human label via marketStore lookup; fall
                      // back to a short hex preview only when the relevant
                      // sport hasn't loaded yet (next poll re-resolves).
                      const polyDesc = market.polyDesc;
                      const label = polyDesc
                        ? `${polyDesc.matchup} (${polyDesc.betTeam}) ${polyDesc.side.toUpperCase()}`
                        : assetId.length > 14
                          ? `${assetId.slice(0, 6)}…${assetId.slice(-4)}`
                          : assetId;
                      const polyHref = polyTableLegacyGameHref(polyDesc);
                      // #248: chip only. `polyClickable` and the row's click
                      // still ride `polyHref`, the pre-change destination.
                      // side stays null on purpose: `polyDesc.side` is the HELD
                      // TOKEN's side ("betTeam wins/loses"), not the slug/ledger
                      // orientation the URL's `side` means — pairing the two is
                      // the inversion the positions table warns about. The token
                      // in `assetId` proves the orientation, or nothing does.
                      const polyChipHref = orderChipHref(
                        { venue: 'polymarket', ticker: assetId, side: null },
                        polyHref,
                      );
                      // Live market data + queue metrics — identical recipe to
                      // buildPolyOrderVM so card and table can't disagree.
                      // #731 — resolved once in the parent; see
                      // resolveOrderTableRowMarket above.
                      const lastCents = market.polyLastCents;
                      const polyQueue = market.polyQueue;
                      const polyQueueAbove = market.polyQueueAbove;
                      const polyBookDiff = market.polyBookDiff;
                      const activeInSale = filling && market.polyQueueAbove === 0;
                      const polyQueueTint = queueRowTone(polyQueue, market.polyQueueTotal);
                      // Clickability needs a real destination: the game
                      // href (moneyline descs carry 'A @ B' matchups; the
                      // panel-target sweep is also moneyline-only). Frame-
                      // fallback descs (spread/total) resolve LABELS but
                      // have no trade-panel surface yet — honest tooltip
                      // instead of a dead pointer.
                      const polyClickable = polyHref !== null;
                      // Same-game highlight — same resolution the card views use.
                      const sgRow = sameGameRow({
                        venue: 'polymarket',
                        ticker: assetId,
                        key: `poly-${id}`,
                      });
                      return (
                        <tr
                          key={`poly-${id}`}
                          data-view-anchor={viewAnchor}
                          onMouseEnter={sgRow.onMouseEnter}
                          onMouseLeave={sgRow.onMouseLeave}
                          className={`border-border/40 hover:bg-muted/30 border-b ${
                            polyClickable ? 'cursor-pointer' : ''
                          } ${polyQueueTint} ${
                            activeInSale ? 'border-l-4 border-l-amber-400/80' : ''
                          } ${
                            market.velocitySeverity === 'alert'
                              ? 'animate-velocity-spike-alert'
                              : market.velocitySeverity === 'warn'
                                ? 'animate-velocity-spike-warn'
                                : ''
                          } ${sgRow.className}`}
                          title={
                            polyClickable
                              ? assetId
                              : polyDesc
                                ? `${assetId} — no trade panel for this market type yet (spread/total); edit/cancel from the row actions`
                                : `${assetId} — can't open the trade panel: market not in the live feed (open its sport tab; you can still cancel from the row actions)`
                          }
                          onClick={
                            polyClickable ? () => openPolyOrderPanel(assetId, polyHref) : undefined
                          }
                        >
                          <td className="text-foreground px-4 py-2 font-mono text-xs">
                            {label}
                            {polyChipHref && (
                              <GameDetailLink href={polyChipHref} className="-my-1 ml-1" />
                            )}
                            <EventStateWarning
                              certainty={market.certainty}
                              className="mt-1"
                            />
                          </td>
                          <td className="px-2 py-2 text-center">
                            <VenueChip venue="polymarket" />
                          </td>
                          <td className="px-3 py-2 text-center">
                            {polyDesc ? (
                              <span
                                className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                                  polyDesc.side === 'yes'
                                    ? 'bg-green-500/20 text-green-400'
                                    : 'bg-red-500/20 text-red-400'
                                }`}
                              >
                                ({polyDesc.betTeam}) {polyDesc.side.toUpperCase()}
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </td>
                          {/* Card↔table parity for #557: the poly order CARD
                              renders the notice box off the same predicate
                              (buildPolyOrderVM), so this row shows the marker
                              shape of the identical finding. */}
                          <td className="px-3 py-2 text-center text-xs">
                            <PolyOrderTifCell
                              order={o}
                              eventStartMs={market.polyEventStartMs}
                              activeInSale={activeInSale}
                            />
                          </td>
                          <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                            {remaining}
                          </td>
                          <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                            <PriceWithOddsInline quote={priceQuote} />
                          </td>
                          {/* Last — side-native (inverted for the slug's NO
                              token inside polyOrderLiveData). */}
                          <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                            <PriceWithOddsInline
                              quote={openOrderQuote({
                                priceCents: lastCents,
                                feeAdjusted: false,
                                makerRate: 0,
                                isSell,
                              })}
                              muted
                            />
                          </td>
                          {/* 1m Δ — priceVelocity has no source for Polymarket
                              tickers (marketStore doesn't surface poly mid
                              yet). Dash placeholder for column alignment. */}
                          <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                            —
                          </td>
                          {/* Book — BUY only: a resting SELL's queue lives on
                              the opposite token's book (close-path only), so
                              claiming a distance would risk a wrong number. */}
                          <td className="px-3 py-2 text-center font-mono text-xs">
                            {polyBookDiff == null ? (
                              <span className="text-muted-foreground">—</span>
                            ) : (
                              <span
                                className={`inline-flex items-center gap-1.5 ${
                                  polyBookDiff === 0
                                    ? 'text-green-400'
                                    : polyBookDiff === 1
                                      ? 'text-yellow-400'
                                      : 'text-red-400'
                                }`}
                              >
                                <span
                                  className={`inline-block h-2 w-2 rounded-full ${
                                    polyBookDiff === 0
                                      ? 'bg-green-400'
                                      : polyBookDiff === 1
                                        ? 'bg-yellow-400'
                                        : 'bg-red-400'
                                  }`}
                                />
                                {polyBookDiff === 0 ? 'Top' : `-${polyBookDiff}`}
                              </span>
                            )}
                          </td>
                          {/* Queue (pessimistic upper bound — whole-level depth
                              at the order's price) + level above (exact). */}
                          <td
                            className={`px-3 py-2 text-right font-mono text-xs ${queueTone(polyQueue, market.polyQueueTotal)} ${queueValuePulse(polyQueue)}`}
                          >
                            {polyQueue == null
                              ? '—'
                              : polyQueue < 1
                                ? 'Front'
                                : Math.round(polyQueue).toLocaleString()}
                          </td>
                          <td
                            className={`px-3 py-2 text-right font-mono text-xs ${queueTone(polyQueueAbove, market.polyQueueTotal)}`}
                          >
                            {polyQueueAbove == null
                              ? '—'
                              : Math.round(polyQueueAbove).toLocaleString()}
                          </td>
                          {/* In line behind — genuinely unknowable on the CLOB
                              (no per-order FIFO slot), not merely unwired. */}
                          <td
                            className="text-muted-foreground px-3 py-2 text-right font-mono text-xs"
                            title="Polymarket's book has no per-order FIFO slot — depth behind your order can't be known"
                          >
                            —
                          </td>
                          {/* Cost + To Win — mirrors buildPolyOrderVM via the
                              shared openOrderEconomics helper. Polymarket has
                              no maker fee in our model (rate 0), so cost/to-win
                              equal the gross — but routing through the same
                              helper keeps the Kalshi/poly math in lockstep. */}
                          {(() => {
                            const economicQty = filling ? matched : orig;
                            const { costDollars: cost, toWinDollars: toWin } =
                              openOrderEconomics({
                                economicQty,
                                bidCents: priceCents,
                                makerRate: 0,
                                isSell,
                              });
                            return (
                              <>
                                <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                                  {cost != null ? fmtUsd(cost) : '—'}
                                </td>
                                <td className="text-positive px-3 py-2 text-right font-mono text-xs">
                                  {toWin != null ? fmtUsd(toWin) : '—'}
                                </td>
                              </>
                            );
                          })()}
                          <td className="px-2 py-2">
                            <div className="flex items-center gap-0.5">
                            <VelocityMuteToggle
                              muted={market.muted}
                              onToggle={() => toggleVelocityMute(`poly-${id}`)}
                            />
                            <PinToggle
                              pinned={market.pinned}
                              onToggle={() =>
                                togglePin({ key: `poly-${id}`, kind: 'order', label })
                              }
                            />
                            <OrderActionButtons
                              canceling={market.canceling}
                              unconfirmed={market.cancelUnconfirmed}
                              cancelTitle={market.cancelTitle}
                              onCancel={() =>
                                requestCancelOrder({
                                  venue: 'polymarket',
                                  orderId: id,
                                  label,
                                })
                              }
                              onEdit={() => {
                                // Side comes from the marketStore lookup when
                                // the market is loaded; falls back to 'yes' as
                                // a benign placeholder for the modal's badge
                                setEditOrder({
                                  venue: 'polymarket',
                                  orderId: id,
                                  marketLabel: label,
                                  side: polyDesc?.side ?? 'yes',
                                  action: isSell ? 'sell' : 'buy',
                                  remainingCount: remaining,
                                  currentPriceCents: priceCents ?? 50,
                                  ticker: assetId,
                                  tickSize: polyDesc?.tickSize,
                                  negRisk: polyDesc?.negRisk,
                                });
                              }}
                            />
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    const order = row.order;
                    const { marketLabel, betTeam, titleTeam } = describeMarket(order.ticker, order.side);
                    const timeLabel = formatStartTimeLocal(row.sortStart);
                    const href = gameDetailHref(order.ticker, order.side);
                    // #248: chip only. The row's click, tooltip and hover
                    // affordance stay on `href`, the pre-change destination.
                    const chipHref = orderChipHref(
                      { venue: 'kalshi', ticker: order.ticker, side: order.side },
                      href,
                    );
                    const label = `${marketLabel}${timeLabel ? ` ${timeLabel}` : ''}`;
                    // Kalshi often returns only yes_price even for NO orders; for
                    // a NO bid the actual price the user placed is 100 - yes_price
                    // (exact mirror — sub-cent safe).
                    const bidCents =
                      order.side === 'yes'
                        ? (order.yesPrice ?? (order.noPrice != null ? mirrorCents(order.noPrice) : null))
                        : (order.noPrice ?? (order.yesPrice != null ? mirrorCents(order.yesPrice) : null));
                    // Last trade comes from hydra (YES-cents); invert for NO orders.
                    const lastYes = market.lastYes;
                    const lastSide =
                      lastYes == null ? null : order.side === 'yes' ? lastYes : mirrorCents(lastYes);
                    const makerRate = market.makerRate;
                    const { filledQty, filling } = kalshiOrderFillStats(
                      order,
                      market.filledQty,
                    );
                    // Quantity the Cost / To Win totals bill against (the
                    // filled portion once anything has filled). Order TOTALS
                    // only — the quote pairs below are per-contract (#452).
                    const economicQty = filling ? filledQty : order.remainingCount;
                    const isSell = order.action === 'sell';
                    // Bid / Last quote pairs — same one-basis contract the card
                    // renders (#452), on the same Raw / Fees / MM Rebate state
                    // and the same fee direction (a sell quotes what it nets).
                    // `lastSide` goes in unrounded so a sub-cent last trade
                    // keeps its precision (the card's basis, table parity).
                    const quoteArgs = {
                      feeAdjusted: feeAdjustedOdds,
                      makerRate,
                      makerRebatePct: quoteRebatePct,
                      isSell,
                    };
                    const bidQuote = openOrderQuote({ priceCents: bidCents, ...quoteArgs });
                    const lastQuote = openOrderQuote({ priceCents: lastSide, ...quoteArgs });
                    // Book reference depends on the order's direction:
                    //   • BUY (bid)  → max bid on the same side; behind the
                    //                  top means the user's price is below
                    //                  it. Diff = topBid − orderPrice; show
                    //                  Top / −1 / −N (ladder goes down).
                    //   • SELL (ask) → lowest ask on the same side, derived
                    //                  as 100 − max(opposite-side bid). Behind
                    //                  the top means the user's price is above
                    //                  it. Diff = orderPrice − topAsk; show
                    //                  Top / +1 / +N (ladder goes up).
                    // #731 — the book contributes exactly one number, resolved
                    // once in the parent; see resolveOrderTableRowMarket above.
                    const topRef = market.topRef;
                    const bookDiff =
                      topRef != null && bidCents != null
                        ? Math.max(0, isSell ? bidCents - topRef : topRef - bidCents)
                        : null;
                    const queue = market.queue;
                    const queueAbove = market.queueAbove;
                    const queueBehind = market.queueBehind;
                    const total = market.queueTotal;
                    // Row tint + value pulse track the cumulative `queue` —
                    // that's the metric a trader's eyes should snap to.
                    // `level above` keeps the queue color scale; `in line
                    // behind` is inverted because more depth behind you is good.
                    const queueTint = queueRowTone(queue, total);
                    const queueTextTone = queueTone(queue, total);
                    const queueAboveTone = queueTone(queueAbove, total);
                    const queueBehindTextTone = queueBehindTone(queueBehind);
                    const queuePulse = queueValuePulse(queue);
                    const activeInSale = filling && queueAbove === 0;
                    const divergence = market.divergence;
                    const sgRow = sameGameRow({
                      venue: 'kalshi',
                      ticker: order.ticker,
                      key: order.orderId,
                    });
                    return (
                    <tr
                      key={order.orderId}
                      data-view-anchor={viewAnchor}
                      onMouseEnter={sgRow.onMouseEnter}
                      onMouseLeave={sgRow.onMouseLeave}
                      className={`border-border/40 hover:bg-muted/30 cursor-pointer border-b ${queueTint} ${
                        activeInSale ? 'border-l-4 border-l-amber-400/80' : ''
                      } ${
                        market.velocitySeverity === 'alert'
                          ? 'animate-velocity-spike-alert'
                          : market.velocitySeverity === 'warn'
                            ? 'animate-velocity-spike-warn'
                            : ''
                      } ${
                        market.divergenceFlash ? 'animate-divergence-flash' : ''
                      } ${sgRow.className}`}
                      // The click is no longer gated on `href` (#214), matching
                      // the positions row since #98. The tooltip therefore has
                      // to stop claiming the market is absent whenever there is
                      // no game page — a live boxing/tennis market has no game
                      // page and opens the panel fine. `inLiveFeed` is a Map
                      // lookup, deliberately NOT a second resolveBuyPanelTarget
                      // sweep per row per render.
                      title={
                        href || market.liveFeed
                          ? order.ticker
                          : `${order.ticker} — can't open the trade panel: market not in the live feed and the ticker doesn't map to a game page (cancel still works from the row actions)`
                      }
                      onClick={orderCardOpen(
                        { venue: 'kalshi', ticker: order.ticker, side: order.side },
                        href,
                      )}
                    >
                      <td className="text-foreground px-4 py-2 font-mono text-xs">
                        <span
                          className={href ? 'hover:text-blue-400 hover:underline' : undefined}
                        >
                          <BetTeamTitle title={label} team={titleTeam} />
                        </span>
                        {chipHref && <GameDetailLink href={chipHref} className="-my-1 ml-1" />}
                        <DivergenceBadge info={divergence} className="ml-1.5 align-middle" />
                        <EventStateWarning
                          certainty={market.certainty}
                          className="mt-1"
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <VenueChip venue="kalshi" />
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            order.side === 'yes'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {betTeam ? `(${betTeam}) ` : ''}{order.side.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center text-xs">
                        <OrderTifCell
                          action={order.action}
                          expirationTs={order.expirationTs}
                          // `sortStartMs` returns POSITIVE_INFINITY for an
                          // unknown start (it sorts unknowns last), so the
                          // guard must be isFinite — `> 0` would let Infinity
                          // through and every order with an expiry but no known
                          // start would read as expiring before it. Same idiom
                          // as the order-card call sites above.
                          eventStartMs={
                            Number.isFinite(row.sortStart) ? row.sortStart : null
                          }
                          activeInSale={activeInSale}
                        />
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        {order.remainingCount}
                      </td>
                      <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                        <PriceWithOddsInline quote={bidQuote} />
                      </td>
                      <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                        <PriceWithOddsInline quote={lastQuote} muted />
                      </td>
                      {/* 1m Δ — same side-native chip math as the OrderCard,
                          surfaced as a table cell for card↔table parity. */}
                      {(() => {
                        const delta = sideNativePriceDelta(
                          market.priceVelocityM1YesCents,
                          order.side,
                        );
                        if (delta == null) {
                          return (
                            <td className="text-muted-foreground px-3 py-2 text-right font-mono text-xs">
                              —
                            </td>
                          );
                        }
                        const tone =
                          delta > 0
                            ? 'text-green-400'
                            : delta < 0
                              ? 'text-red-400'
                              : 'text-muted-foreground';
                        return (
                          <td className={`px-3 py-2 text-right font-mono text-xs ${tone}`}>
                            {delta >= 0 ? '+' : ''}{delta}¢
                          </td>
                        );
                      })()}
                      <td className="px-3 py-2 text-center font-mono text-xs">
                        <span aria-hidden={!market.bookRetained} className={`inline-block w-[9ch] whitespace-nowrap text-[9px] text-amber-400 ${market.bookRetained ? '' : 'invisible'}`}>Last book</span>
                            {bookDiff == null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <span
                            className={`inline-flex items-center gap-1.5 ${
                              bookDiff === 0
                                ? 'text-green-400'
                                : bookDiff === 1
                                  ? 'text-yellow-400'
                                  : 'text-red-400'
                            }`}
                            title={
                              topRef != null
                                ? `Top ${isSell ? 'ask' : 'bid'} price ${topRef}¢; queue shows FIFO depth at this price`
                                : undefined
                            }
                          >
                            <span
                              className={`inline-block h-2 w-2 rounded-full ${
                                bookDiff === 0
                                  ? 'bg-green-400'
                                  : bookDiff === 1
                                    ? 'bg-yellow-400'
                                    : 'bg-red-400'
                              }`}
                            />
                            {bookDiff === 0 ? 'Top' : `${isSell ? '+' : '-'}${bookDiff}`}
                          </span>
                        )}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs ${queueTextTone} ${queuePulse}`}
                        title={
                          queue == null
                            ? 'Queue data is temporarily unavailable; the order itself remains current'
                            : undefined
                        }
                      >
                        {queue == null
                          ? '—'
                          : queue < 1
                            ? 'Front'
                            : Math.round(queue).toLocaleString()}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs ${queueAboveTone}`}
                        title={
                          queueAbove == null
                            ? 'Queue data is temporarily unavailable; the order itself remains current'
                            : undefined
                        }
                      >
                        {queueAbove == null
                          ? '—'
                          : Math.round(queueAbove).toLocaleString()}
                      </td>
                      <td
                        className={`px-3 py-2 text-right font-mono text-xs ${queueBehindTextTone}`}
                        title={
                          queueBehind == null
                            ? 'Queue data is temporarily unavailable; the order itself remains current'
                            : undefined
                        }
                      >
                        {queueBehind == null
                          ? '—'
                          : Math.round(queueBehind).toLocaleString()}
                      </td>
                      {/* Cost + To Win — parity with OrderCard footer. Uses
                          the shared openOrderEconomics helper so the fee-
                          inclusive math stays in lockstep with the card view:
                          a resting limit bills as a maker fill, so the per-
                          series maker fee folds into cost (+fee) / to-win
                          (−fee). bidCents is side-native already. GROSS fee —
                          the MM rebate reaches the quote pair above and stops
                          there, so these totals are unchanged by the toggle
                          (card parity: buildKalshiOrderVM does the same). */}
                      {(() => {
                        const { costDollars: cost, toWinDollars: toWin } =
                          openOrderEconomics({
                            economicQty,
                            bidCents,
                            makerRate,
                            isSell,
                          });
                        return (
                          <>
                            <td className="text-foreground px-3 py-2 text-right font-mono text-xs">
                              {cost != null ? fmtUsd(cost) : '—'}
                            </td>
                            <td className="text-positive px-3 py-2 text-right font-mono text-xs">
                              {toWin != null ? fmtUsd(toWin) : '—'}
                            </td>
                          </>
                        );
                      })()}
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-0.5">
                        <VelocityMuteToggle
                          muted={market.muted}
                          onToggle={() => toggleVelocityMute(order.orderId)}
                        />
                        <PinToggle
                          pinned={market.pinned}
                          onToggle={() =>
                            togglePin({ key: order.orderId, kind: 'order', label })
                          }
                        />
                        <OrderActionButtons
                          canceling={market.canceling}
                          onCancel={() =>
                            requestCancelOrder({
                              venue: 'kalshi',
                              orderId: order.orderId,
                              label,
                            })
                          }
                          onEdit={() => {
                            // bidCents is the side-native current price computed
                            // a few lines up. Fall back to 50¢ midpoint only if
                            // the orderbook hasn't seeded it yet — the trader
                            // will overtype anyway.
                            setEditOrder({
                              venue: 'kalshi',
                              orderId: order.orderId,
                              marketLabel: betTeam
                                ? `${marketLabel} (${betTeam})`
                                : marketLabel,
                              side: order.side,
                              action: order.action,
                              remainingCount: order.remainingCount,
                              currentPriceCents: bidCents ?? 50,
                              ticker: order.ticker,
                            });
                          }}
                          editAvailable={order.reconciliation?.actions.decrease !== 'unavailable'}
                        />
                        </div>
                      </td>
                    </tr>
                    );
}

export const OrderTableRow = memo(OrderTableRowInner);
